import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTheme, useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { toast } from "sonner";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { 
  Plane, 
  MapPin, 
  Calendar as CalendarIcon, 
  Ticket, 
  Plus, 
  Sun, 
  Moon, 
  LogOut,
  ChevronRight,
  Loader2,
  Menu,
  X
} from "lucide-react";
import AIAssistant from "../components/AIAssistant";
import Notifications from "../components/Notifications";

const Dashboard = () => {
  const { theme, toggleTheme } = useTheme();
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const [trips, setTrips] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [newTrip, setNewTrip] = useState({
    title: "",
    destination: "",
    description: "",
    start_date: null,
    end_date: null
  });
  const [creating, setCreating] = useState(false);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [tripsRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/trips`, { headers }),
        axios.get(`${API}/bookings`, { headers })
      ]);
      setTrips(tripsRes.data);
      setBookings(bookingsRes.data);
    } catch (error) {
      toast.error("Errore nel caricamento dei dati");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchData();
  }, [token]);

  const handleCreateTrip = async () => {
    if (!newTrip.title || !newTrip.destination || !newTrip.start_date || !newTrip.end_date) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }
    
    setCreating(true);
    try {
      const response = await axios.post(`${API}/trips`, {
        ...newTrip,
        start_date: format(newTrip.start_date, "yyyy-MM-dd"),
        end_date: format(newTrip.end_date, "yyyy-MM-dd")
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setTrips([response.data, ...trips]);
      setCreateDialogOpen(false);
      setNewTrip({ title: "", destination: "", description: "", start_date: null, end_date: null });
      toast.success("Viaggio creato con successo!");
      navigate(`/trip/${response.data.trip_id}`);
    } catch (error) {
      toast.error("Errore nella creazione del viaggio");
    } finally {
      setCreating(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const upcomingTrips = trips.filter(t => new Date(t.start_date) >= new Date()).slice(0, 3);
  const recentBookings = bookings.slice(0, 4);

  const getBookingIcon = (type) => {
    switch(type) {
      case "flight": return "✈️";
      case "train": return "🚄";
      case "bus": return "🚌";
      case "hotel": return "🏨";
      case "airbnb": return "🏠";
      default: return "🎫";
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Top Navbar */}
      <nav className="sticky top-0 z-40 glass border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon" 
                className="md:hidden"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-nav-toggle"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
              <Link to="/" className="flex items-center gap-2" data-testid="dashboard-logo">
                <Plane className="w-6 h-6 text-primary" />
                <span className="font-heading text-lg font-bold hidden sm:inline">TravelPro</span>
              </Link>
            </div>

            <div className="hidden md:flex items-center gap-6">
              <Link to="/dashboard" className="text-sm font-medium text-primary" data-testid="nav-dashboard-link">
                Dashboard
              </Link>
              <Link to="/bookings" className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-bookings-link">
                Prenotazioni
              </Link>
              <Link to="/contact" className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-contact-link">
                Contatti
              </Link>
            </div>

            <div className="flex items-center gap-3">
              <Notifications />
              <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="dashboard-theme-toggle">
                {theme === "light" ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
              </Button>
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary">
                <span className="text-sm font-medium">{user?.name?.split(' ')[0] || 'Utente'}</span>
              </div>
              <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-border bg-background/95 backdrop-blur-xl px-4 py-4 space-y-2">
            <Link to="/dashboard" className="block py-2 font-medium text-primary" onClick={() => setMobileMenuOpen(false)}>
              Dashboard
            </Link>
            <Link to="/bookings" className="block py-2 text-muted-foreground" onClick={() => setMobileMenuOpen(false)}>
              Prenotazioni
            </Link>
            <Link to="/contact" className="block py-2 text-muted-foreground" onClick={() => setMobileMenuOpen(false)}>
              Contatti
            </Link>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold">
              Ciao, {user?.name?.split(' ')[0] || 'Viaggiatore'}!
            </h1>
            <p className="text-muted-foreground mt-1">Ecco il riepilogo dei tuoi viaggi</p>
          </div>
          
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full" data-testid="create-trip-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nuovo Viaggio
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">Crea nuovo viaggio</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="title">Titolo *</Label>
                  <Input 
                    id="title"
                    placeholder="Es. Vacanza estiva 2024"
                    value={newTrip.title}
                    onChange={(e) => setNewTrip({...newTrip, title: e.target.value})}
                    data-testid="trip-title-input"
                  />
                </div>
                <div>
                  <Label htmlFor="destination">Destinazione *</Label>
                  <Input 
                    id="destination"
                    placeholder="Es. Roma, Italia"
                    value={newTrip.destination}
                    onChange={(e) => setNewTrip({...newTrip, destination: e.target.value})}
                    data-testid="trip-destination-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Data inizio *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start font-normal" data-testid="trip-start-date-btn">
                          <CalendarIcon className="w-4 h-4 mr-2" />
                          {newTrip.start_date ? format(newTrip.start_date, "dd MMM yyyy", { locale: it }) : "Seleziona"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={newTrip.start_date}
                          onSelect={(date) => setNewTrip({...newTrip, start_date: date})}
                          locale={it}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label>Data fine *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start font-normal" data-testid="trip-end-date-btn">
                          <CalendarIcon className="w-4 h-4 mr-2" />
                          {newTrip.end_date ? format(newTrip.end_date, "dd MMM yyyy", { locale: it }) : "Seleziona"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={newTrip.end_date}
                          onSelect={(date) => setNewTrip({...newTrip, end_date: date})}
                          locale={it}
                          disabled={(date) => newTrip.start_date && date < newTrip.start_date}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
                <div>
                  <Label htmlFor="description">Descrizione</Label>
                  <Textarea 
                    id="description"
                    placeholder="Aggiungi una descrizione..."
                    value={newTrip.description}
                    onChange={(e) => setNewTrip({...newTrip, description: e.target.value})}
                    data-testid="trip-description-input"
                  />
                </div>
                <Button 
                  className="w-full" 
                  onClick={handleCreateTrip}
                  disabled={creating}
                  data-testid="trip-submit-btn"
                >
                  {creating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Crea Viaggio
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Stats Cards */}
            <div className="lg:col-span-3 grid grid-cols-2 sm:grid-cols-4 gap-4">
              <Card className="border-border">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Plane className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold font-mono">{trips.length}</p>
                      <p className="text-xs text-muted-foreground">Viaggi totali</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-border">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center">
                      <CalendarIcon className="w-5 h-5 text-accent" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold font-mono">{upcomingTrips.length}</p>
                      <p className="text-xs text-muted-foreground">In programma</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-border">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center">
                      <Ticket className="w-5 h-5 text-secondary-foreground" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold font-mono">{bookings.length}</p>
                      <p className="text-xs text-muted-foreground">Prenotazioni</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-border">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold font-mono">
                        {[...new Set(trips.map(t => t.destination.split(',')[0]))].length}
                      </p>
                      <p className="text-xs text-muted-foreground">Destinazioni</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Upcoming Trips */}
            <div className="lg:col-span-2">
              <Card className="border-border h-full">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="font-heading text-lg">Prossimi Viaggi</CardTitle>
                  {trips.length > 0 && (
                    <Button variant="ghost" size="sm" onClick={() => {}} data-testid="view-all-trips">
                      Vedi tutti
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  )}
                </CardHeader>
                <CardContent>
                  {upcomingTrips.length === 0 ? (
                    <div className="text-center py-8">
                      <Plane className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                      <p className="text-muted-foreground">Nessun viaggio in programma</p>
                      <Button 
                        variant="outline" 
                        className="mt-4"
                        onClick={() => setCreateDialogOpen(true)}
                        data-testid="empty-create-trip"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Crea il tuo primo viaggio
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {upcomingTrips.map((trip) => (
                        <div 
                          key={trip.trip_id}
                          onClick={() => navigate(`/trip/${trip.trip_id}`)}
                          className="flex items-center gap-4 p-4 rounded-xl bg-secondary/30 hover:bg-secondary/50 cursor-pointer transition-colors"
                          data-testid={`trip-card-${trip.trip_id}`}
                        >
                          <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                            <MapPin className="w-6 h-6 text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold truncate">{trip.title}</h3>
                            <p className="text-sm text-muted-foreground truncate">{trip.destination}</p>
                          </div>
                          <div className="text-right shrink-0">
                            <p className="font-mono text-sm">{format(new Date(trip.start_date), "dd MMM", { locale: it })}</p>
                            <p className="text-xs text-muted-foreground">
                              {Math.ceil((new Date(trip.start_date) - new Date()) / (1000 * 60 * 60 * 24))} giorni
                            </p>
                          </div>
                          <ChevronRight className="w-5 h-5 text-muted-foreground" />
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Recent Bookings */}
            <div className="lg:col-span-1">
              <Card className="border-border h-full">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="font-heading text-lg">Prenotazioni</CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => navigate("/bookings")} data-testid="view-all-bookings">
                    Vedi tutte
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </CardHeader>
                <CardContent>
                  {recentBookings.length === 0 ? (
                    <div className="text-center py-8">
                      <Ticket className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                      <p className="text-muted-foreground text-sm">Nessuna prenotazione</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {recentBookings.map((booking) => (
                        <div 
                          key={booking.booking_id}
                          className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-secondary/30 cursor-pointer transition-colors"
                          onClick={() => navigate("/bookings")}
                          data-testid={`booking-card-${booking.booking_id}`}
                        >
                          <span className="text-2xl">{getBookingIcon(booking.type)}</span>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{booking.title}</p>
                            <p className="text-xs text-muted-foreground font-mono">
                              {format(new Date(booking.booking_date), "dd MMM yyyy", { locale: it })}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </main>

      {/* AI Assistant */}
      <AIAssistant />
    </div>
  );
};

export default Dashboard;
